import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface UseAuctionsProps {
  page: number;
  limit: number;
}

const sortAuctionsByDate = (auctions: any[]) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return auctions.sort((a, b) => {
    const aDate = a.auction_date ? new Date(a.auction_date + 'T00:00:00') : null;
    const bDate = b.auction_date ? new Date(b.auction_date + 'T00:00:00') : null;
    const aFuture = aDate && aDate >= today;
    const bFuture = bDate && bDate >= today;
    const aHasDate = aDate !== null;
    const bHasDate = bDate !== null;

    // Future dates first, then past dates, then no date
    if (aFuture && !bFuture) return -1;
    if (!aFuture && bFuture) return 1;
    if (aFuture && bFuture) return aDate!.getTime() - bDate!.getTime(); // soonest first
    if (aHasDate && !bHasDate) return -1;
    if (!aHasDate && bHasDate) return 1;
    if (aHasDate && bHasDate) return bDate!.getTime() - aDate!.getTime(); // most recent past first
    // Both no date: by created_at desc
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });
};

export const useAuctions = ({ page, limit }: UseAuctionsProps) => {
  return useQuery({
    queryKey: ['auctions', page, limit],
    queryFn: async () => {
      // 1. Fetch top auction IDs in display order
      const { data: topAuctionRows, error: topError } = await supabase
        .from('top_auctions')
        .select('announcement_id')
        .order('display_order', { ascending: true });

      if (topError) throw topError;

      const topIds = (topAuctionRows || []).map(r => r.announcement_id);
      const topCount = topIds.length;

      // 2. Get total count of all announcements
      const { count, error: countError } = await supabase
        .from('announcements')
        .select('*', { count: 'exact', head: true });

      if (countError) throw countError;

      let topAuctions: any[] = [];
      let regularAuctions: any[] = [];

      if (page === 1) {
        // Fetch top auctions by their IDs
        if (topIds.length > 0) {
          const { data: topData, error: topDataError } = await supabase
            .from('announcements')
            .select('*')
            .in('id', topIds);

          if (topDataError) throw topDataError;

          // Sort by display_order (same order as topIds)
          const idOrder = new Map(topIds.map((id, i) => [id, i]));
          topAuctions = (topData || []).sort((a, b) => (idOrder.get(a.id) ?? 0) - (idOrder.get(b.id) ?? 0));
        }

        // Fill remaining slots with regular auctions
        const remainingSlots = limit - topAuctions.length;
        if (remainingSlots > 0) {
          let query = supabase
            .from('announcements')
            .select('*')
            .limit(remainingSlots);

          if (topIds.length > 0) {
            query = query.not('id', 'in', `(${topIds.join(',')})`);
          }

          const { data: regData, error: regError } = await query;
          if (regError) throw regError;
          regularAuctions = sortAuctionsByDate(regData || []);
        }
      } else {
        // Page 2+: only regular auctions, offset adjusted
        const regularOnPage1 = Math.max(0, limit - topCount);
        const offset = regularOnPage1 + (page - 2) * limit;

        let query = supabase
          .from('announcements')
          .select('*')
          .range(offset, offset + limit - 1);

        if (topIds.length > 0) {
          query = query.not('id', 'in', `(${topIds.join(',')})`);
        }

        const { data: regData, error: regError } = await query;
        if (regError) throw regError;
        regularAuctions = sortAuctionsByDate(regData || []);
      }
      const allData = [...topAuctions, ...regularAuctions];

      if (allData.length === 0) {
        return { auctions: [], totalCount: count || 0 };
      }

      // Fetch breeder profiles
      const breederIds = [...new Set(allData.map(a => a.breeder_id))];
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, business_name, profile_image_url')
        .in('id', breederIds);

      if (profilesError) throw profilesError;

      const profileMap = new Map(profiles?.map(p => [p.id, p]) || []);

      const auctions = allData.map(a => ({
        ...a,
        profiles: profileMap.get(a.breeder_id) || null,
      }));

      return {
        auctions,
        totalCount: count || 0,
      };
    },
  });
};
